import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PiClock,
  PiWarning,
  PiTrendUp,
  PiArrowRight,
  PiThumbsUp,
  PiThumbsDown,
  PiThumbsUpFill,
  PiThumbsDownFill,
} from 'react-icons/pi'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import type {
  EnrichedAction,
  SentimentType,
} from '../../services/action_summary_api'
import {
  RISK_STYLES,
  URGENCY_ICONS,
  URGENCY_COLORS,
  RISK_TOOLTIPS,
  URGENCY_TOOLTIPS,
} from './constants'

interface EnrichedActionCardProps {
  action: EnrichedAction
  /** Optional callback when the card is clicked (for tracking) */
  onCardClick?: () => void
  /** Optional callback when user provides sentiment feedback (thumbs up/down) */
  onSentimentClick?: (sentiment: SentimentType) => void
  /** Initial sentiment value (from persisted data) */
  initialSentiment?: SentimentType | null
}

/**
 * Signal badge component showing "why now" indicators.
 */
interface SignalBadgeProps {
  icon: React.ReactNode
  label: string
  variant: 'danger' | 'warning' | 'info'
}

const badgeVariants = {
  danger: 'bg-red-50 text-red-600 border-red-100',
  warning: 'bg-amber-50 text-amber-600 border-amber-100',
  info: 'bg-gray-50 text-gray-600 border-gray-100',
}

const SignalBadge: React.FC<SignalBadgeProps> = ({ icon, label, variant }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
      badgeVariants[variant]
    )}
  >
    {icon}
    {label}
  </span>
)

/**
 * Get risk level badge configuration.
 * Always shows the risk level to make priority clear to users.
 *
 * Risk levels indicate the AI's assessment of deal health:
 * - at_risk: Deal showing warning signs, needs immediate attention
 * - opportunity: Deal has positive momentum, ready to advance
 * - routine: Standard follow-up to keep deal progressing
 */
const getRiskBadge = (riskLevel: string): SignalBadgeProps => {
  switch (riskLevel) {
    case 'at_risk':
      return {
        icon: <PiWarning size={12} />,
        label: 'Needs Attention',
        variant: 'danger',
      }
    case 'opportunity':
      return {
        icon: <PiTrendUp size={12} />,
        label: 'Ready to Advance',
        variant: 'warning',
      }
    default:
      return {
        icon: <PiClock size={12} />,
        label: 'Follow Up',
        variant: 'info',
      }
  }
}

/**
 * Compact action card with visual risk indicators and company link.
 *
 * Features:
 * - Color-coded risk borders (red=at_risk, yellow=opportunity, gray=routine)
 * - Signal badges showing "why now" reasoning
 * - Link to company page
 * - Context snippets from last interaction
 */
export const EnrichedActionCard: React.FC<EnrichedActionCardProps> = ({
  action,
  onCardClick,
  onSentimentClick,
  initialSentiment = null,
}) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const [sentiment, setSentiment] = useState<SentimentType | null>(
    initialSentiment
  )
  const textRef = useRef<HTMLParagraphElement>(null)
  const {
    border,
    bg,
    iconColor,
    Icon: RiskIcon,
  } = RISK_STYLES[action.risk_level]
  const UrgencyIcon = URGENCY_ICONS[action.urgency]
  const riskBadge = getRiskBadge(action.risk_level)

  const handleCardClick = () => {
    // Call the tracking callback if provided
    if (onCardClick) {
      onCardClick()
    }
    // Navigate to the company page
    navigate(action.company_link)
  }

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Don't navigate when expanding
    setIsExpanded((prev) => !prev)
  }

  const handleSentimentClick = (
    e: React.MouseEvent,
    newSentiment: SentimentType
  ) => {
    e.stopPropagation() // Don't navigate when providing feedback
    // Toggle off if clicking same sentiment, otherwise set new sentiment
    const finalSentiment = sentiment === newSentiment ? null : newSentiment
    setSentiment(finalSentiment)
    if (finalSentiment && onSentimentClick) {
      onSentimentClick(finalSentiment)
    }
  }

  // Sync sentiment state with initialSentiment when it changes
  useEffect(() => {
    setSentiment(initialSentiment)
  }, [initialSentiment])

  // Detect if text is actually truncated by line-clamp
  useEffect(() => {
    const el = textRef.current
    if (el && !isExpanded) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  }, [action.recommendation, isExpanded])

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'rounded-lg transition-all hover:shadow-md cursor-pointer',
        border,
        bg,
        'p-3'
      )}
    >
      <div className="flex items-start gap-2">
        {/* Risk Icon with Tooltip */}
        <Tooltip
          title={RISK_TOOLTIPS[action.risk_level].title}
          content={RISK_TOOLTIPS[action.risk_level].content}
          placement="top"
          delay={[500, 0]}
        >
          <div className={cn('flex-shrink-0 mt-0.5', iconColor)}>
            <RiskIcon size={20} />
          </div>
        </Tooltip>

        <div className="flex-1 min-w-0">
          {/* Header: Action + Urgency + Company Link */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                {action.action}
              </h4>
              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500">
                <span className="text-fb-dark-green font-medium">
                  {action.company_name}
                </span>
                {action.company_subcategory && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-400">
                      {action.company_subcategory}
                    </span>
                  </>
                )}
                {action.contact_name && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{action.contact_name}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Risk Badge - always shown for clarity */}
              <SignalBadge {...riskBadge} />
              <Tooltip
                title={URGENCY_TOOLTIPS[action.urgency].title}
                content={URGENCY_TOOLTIPS[action.urgency].content}
                placement="top"
                delay={[500, 0]}
              >
                <UrgencyIcon
                  size={14}
                  className={URGENCY_COLORS[action.urgency]}
                />
              </Tooltip>
              <PiArrowRight size={16} className="text-gray-400" />
            </div>
          </div>

          {/* Recommendation - expandable */}
          <p
            ref={textRef}
            onClick={handleExpandClick}
            className={cn(
              'text-xs text-gray-600 mt-1 cursor-pointer',
              !isExpanded && 'line-clamp-4'
            )}
          >
            {action.recommendation}
          </p>
          {!isExpanded && isTruncated && (
            <button
              onClick={handleExpandClick}
              className="text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium mt-0.5"
            >
              Show more
            </button>
          )}

          {/* Context Snippet - only if exists and compact */}
          {action.context_snippet && (
            <p className="text-xs text-gray-400 italic mt-1 line-clamp-1">
              &ldquo;{action.context_snippet}&rdquo;
            </p>
          )}

          {/* Sentiment Feedback */}
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
            <span className="text-xs text-gray-400 mr-1">Helpful?</span>
            <Tooltip
              title="Helpful"
              content="This recommendation is useful"
              placement="top"
              delay={[300, 0]}
            >
              <button
                onClick={(e) => handleSentimentClick(e, 'positive')}
                className={cn(
                  'p-1 rounded transition-colors',
                  sentiment === 'positive'
                    ? 'text-fb-green-600 bg-fb-green-50'
                    : 'text-gray-400 hover:text-fb-green-600 hover:bg-fb-green-50'
                )}
                aria-label="Mark as helpful"
              >
                {sentiment === 'positive' ? (
                  <PiThumbsUpFill size={14} />
                ) : (
                  <PiThumbsUp size={14} />
                )}
              </button>
            </Tooltip>
            <Tooltip
              title="Not helpful"
              content="This recommendation is not useful"
              placement="top"
              delay={[300, 0]}
            >
              <button
                onClick={(e) => handleSentimentClick(e, 'negative')}
                className={cn(
                  'p-1 rounded transition-colors',
                  sentiment === 'negative'
                    ? 'text-red-600 bg-red-50'
                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                )}
                aria-label="Mark as not helpful"
              >
                {sentiment === 'negative' ? (
                  <PiThumbsDownFill size={14} />
                ) : (
                  <PiThumbsDown size={14} />
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  )
}
